/**
 * Pilot surface registry — SSOT for pilotMode surface folding (P1a).
 *
 * Maps logical surface IDs to CP-PILOT-1 killer loop steps and route hints for P1b guards.
 *
 * @see doc/system-completion-plan/01-target/CP_PILOT_1.md
 * @see .cursor/plans/archived/2026-q2/productization-7w/stream-p1-pilot-mode-surface-folding.md
 */

import type { TenantPilotPresetId } from '@shared/types/tenant-pilot-settings';

/** Logical pilot surfaces — stable IDs for menu/route fold (not nav menu keys). */
export const PILOT_SURFACE_IDS = [
  // — enabled for smb_order_ops_v1 (killer loop) —
  'genesis_intake',
  'site_order_form',
  'business_operations_queue',
  'my_tasks',
  'task_evidence',
  'manager_approval',
  'customer_status_projection',
  'owner_dashboard',
  // — hidden for smb_order_ops_v1 (surface fold) —
  'matrix_full_browser',
  'flow_definition_tree_editor',
  'site_studio_advanced',
  'relationship_finance',
  'relationship_governance',
  'conscious_hub',
  'wisdom_page',
  'brain_autonomy_panel',
] as const;

export type PilotSurfaceId = (typeof PILOT_SURFACE_IDS)[number];

export interface PilotSurfaceDefinition {
  id: PilotSurfaceId;
  /** CP-PILOT-1 step refs when applicable */
  cpPilotSteps?: readonly number[];
  /** Primary route hints for P1b menu/route guards */
  routeHints: readonly string[];
  note?: string;
}

export interface PilotPresetSurfaceMap {
  presetId: TenantPilotPresetId;
  enabledSurfaces: readonly PilotSurfaceId[];
  hiddenSurfaces: readonly PilotSurfaceId[];
}

export interface PilotSurfaceRegistry {
  surfaces: readonly PilotSurfaceDefinition[];
  presets: readonly PilotPresetSurfaceMap[];
}

export const PILOT_SURFACE_DEFINITIONS: readonly PilotSurfaceDefinition[] = [
  {
    id: 'genesis_intake',
    cpPilotSteps: [1],
    routeHints: ['/genesis', '/genesis/intake'],
    note: 'Genesis intake — SMB pilot tenant bootstrap',
  },
  {
    id: 'site_order_form',
    cpPilotSteps: [5, 6, 7],
    routeHints: ['/order'],
    note: 'Guest catalog order surface (Site plane)',
  },
  {
    id: 'business_operations_queue',
    cpPilotSteps: [9, 10],
    routeHints: ['/workspace/assignments', '/flow/dispatch', '/flow/assignments'],
    note: 'Operations assignment board + dispatch',
  },
  {
    id: 'my_tasks',
    cpPilotSteps: [10],
    routeHints: ['/workspace'],
    note: 'My Tasks hub — ExecutorSection',
  },
  {
    id: 'task_evidence',
    cpPilotSteps: [11],
    routeHints: ['/workspace/tasks'],
    note: 'Task detail evidence attach',
  },
  {
    id: 'manager_approval',
    cpPilotSteps: [12],
    routeHints: ['/approvals/pending', '/approvals'],
    note: 'Approval inbox',
  },
  {
    id: 'customer_status_projection',
    cpPilotSteps: [7, 12],
    routeHints: ['/portal/pipeline/status', '/status'],
    note: 'Guest/customer order status projection',
  },
  {
    id: 'owner_dashboard',
    cpPilotSteps: [13],
    routeHints: ['/system/dashboard'],
    note: 'Owner visibility — system dashboard + order/judgment read-back (Step 13)',
  },
  {
    id: 'matrix_full_browser',
    routeHints: [
      '/entity',
      '/item',
      '/finance',
      '/space',
      '/knowledge',
      '/appeal',
      '/calendar',
      '/manufacturing',
      '/log',
      '/export',
    ],
    note: '43-subdomain ERP browser — folded in pilot',
  },
  {
    id: 'flow_definition_tree_editor',
    routeHints: ['/flow/definitions', '/flow/new', '/event/flow-definition'],
    note: 'PL5-fd2 Process Layer tree editor',
  },
  {
    id: 'site_studio_advanced',
    routeHints: ['/system/site-studio'],
    note: 'Site Studio canvas — pilot uses Genesis-seeded form only',
  },
  {
    id: 'relationship_finance',
    routeHints: ['/portal/finance'],
    note: 'Relationship portal finance workspace',
  },
  {
    id: 'relationship_governance',
    routeHints: ['/portal/governance'],
    note: 'Relationship portal governance workspace',
  },
  {
    id: 'conscious_hub',
    routeHints: ['/conscious', '/conscious/alerts', '/conscious/suggestions'],
    note: 'Conscious operator hub — folded; owner_dashboard retains read-back subset',
  },
  {
    id: 'wisdom_page',
    routeHints: ['/conscious/wisdom', '/conscious/ask'],
    note: 'Conscious wisdom / ask surfaces',
  },
  {
    id: 'brain_autonomy_panel',
    routeHints: ['/conscious/brain'],
    note: 'Brain autonomy panel — assistive_only blocks mutations (AI0)',
  },
] as const;

/** SMB order-ops pilot — CP-PILOT-1 killer loop surfaces only. */
export const SMB_ORDER_OPS_V1_ENABLED_SURFACES: readonly PilotSurfaceId[] = [
  'genesis_intake',
  'site_order_form',
  'business_operations_queue',
  'my_tasks',
  'task_evidence',
  'manager_approval',
  'customer_status_projection',
  'owner_dashboard',
] as const;

export const SMB_ORDER_OPS_V1_HIDDEN_SURFACES: readonly PilotSurfaceId[] = [
  'matrix_full_browser',
  'flow_definition_tree_editor',
  'site_studio_advanced',
  'relationship_finance',
  'relationship_governance',
  'conscious_hub',
  'wisdom_page',
  'brain_autonomy_panel',
] as const;

export const PILOT_PRESET_SURFACE_MAPS: readonly PilotPresetSurfaceMap[] = [
  {
    presetId: 'smb_order_ops_v1',
    enabledSurfaces: SMB_ORDER_OPS_V1_ENABLED_SURFACES,
    hiddenSurfaces: SMB_ORDER_OPS_V1_HIDDEN_SURFACES,
  },
] as const;

export const PILOT_SURFACE_REGISTRY: PilotSurfaceRegistry = {
  surfaces: PILOT_SURFACE_DEFINITIONS,
  presets: PILOT_PRESET_SURFACE_MAPS,
};

export function isPilotSurfaceId(value: string): value is PilotSurfaceId {
  return (PILOT_SURFACE_IDS as readonly string[]).includes(value);
}

export function getPilotSurfaceDefinition(
  surfaceId: PilotSurfaceId
): PilotSurfaceDefinition | undefined {
  return PILOT_SURFACE_DEFINITIONS.find((entry) => entry.id === surfaceId);
}

export function resolvePilotPresetSurfaceMap(
  presetId: TenantPilotPresetId
): PilotPresetSurfaceMap | undefined {
  return PILOT_PRESET_SURFACE_MAPS.find((entry) => entry.presetId === presetId);
}

export function listPilotSurfaceRouteHints(
  surfaceIds: readonly PilotSurfaceId[]
): readonly string[] {
  const hints = new Set<string>();
  for (const id of surfaceIds) {
    const definition = getPilotSurfaceDefinition(id);
    if (!definition) continue;
    for (const hint of definition.routeHints) {
      hints.add(hint);
    }
  }
  return [...hints];
}
